import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import VendorDirectoryPermissionGate from "../../components/casamap/admin/VendorDirectoryPermissionGate";
import { VENDOR_DIRECTORY_PERMISSIONS } from "../../auth/casamapaVendorDirectoryPermissions";
import {
  getVerificationCase,
  transitionVerificationCase,
  recordVerificationCheck,
  previewVerificationDecision,
  completeVerificationCase,
  addVerificationContactAttempt,
} from "../../api/clientVendorDirectoryVerificationApi";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.verification";

const METHODS = [
  "phone_confirmation",
  "whatsapp_confirmation",
  "business_email_confirmation",
  "official_registry",
  "professional_registry",
  "business_document",
  "physical_visit",
  "manual_research",
  "map_and_address_review",
  "source_record_cross_reference",
  "other",
];

function statusBadgeClass(status) {
  if (status === "completed") return "cvd-badge--published";
  if (["failed", "cancelled", "expired"].includes(status)) return "cvd-badge--suspended";
  if (["ready_for_review", "in_progress"].includes(status)) return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function formatDateTime(value, locale) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function PipelineBar({ label, value, max }) {
  const safeMax = Math.max(max, 1);
  const pct = Math.min(100, Math.round((value / safeMax) * 100));
  return (
    <div className="cvd-run-pipeline__row">
      <div className="cvd-run-pipeline__head">
        <span>{label}</span>
        <strong>
          {value ?? 0} / {max ?? 0}
        </strong>
      </div>
      <div className="cvd-run-pipeline__track" aria-hidden="true">
        <div className="cvd-run-pipeline__fill cvd-run-pipeline__fill--green" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function VerificationCheckCard({ check, caseId, clientViewId, t, locale, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState("manual_research");
  const [notes, setNotes] = useState("");
  const [naReason, setNaReason] = useState("");
  const [showNaForm, setShowNaForm] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const passCheck = async () => {
    setBusy(true);
    setErrorMessage("");
    try {
      await recordVerificationCheck(clientViewId, caseId, check.id, {
        result: "passed",
        verificationMethod: method,
        notes: notes || undefined,
      });
      onUpdated();
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.detail.actionError`));
    } finally {
      setBusy(false);
    }
  };

  const markNotApplicable = async () => {
    if (!naReason.trim()) {
      setErrorMessage(t(`${COPY_PREFIX}.detail.naReasonRequired`));
      return;
    }
    setBusy(true);
    setErrorMessage("");
    try {
      await recordVerificationCheck(clientViewId, caseId, check.id, {
        result: "not_applicable",
        notApplicableReason: naReason.trim(),
        notes: notes || undefined,
      });
      setShowNaForm(false);
      setNaReason("");
      onUpdated();
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.detail.actionError`));
    } finally {
      setBusy(false);
    }
  };

  const isComplete = check.status === "completed";

  return (
    <article className={`cvd-verification-check${isComplete ? " is-complete" : ""}`}>
      <div className="cvd-verification-check__head">
        <div>
          <h3>{check.requirementKey}</h3>
          <p className="muted">
            {check.isRequired ? t(`${COPY_PREFIX}.detail.required`) : t(`${COPY_PREFIX}.detail.optional`)}
          </p>
        </div>
        <span className={`cvd-badge ${isComplete ? "cvd-badge--published" : "cvd-badge--draft"}`}>
          {t(`${COPY_PREFIX}.checkStatus.${check.status}`, {}, check.status)}
        </span>
      </div>

      <dl className="cvd-dl cvd-dl--compact">
        <div>
          <dt>{t(`${COPY_PREFIX}.detail.checkResult`)}</dt>
          <dd>{check.result ? t(`${COPY_PREFIX}.checkResult.${check.result}`, {}, check.result) : "—"}</dd>
        </div>
        <div>
          <dt>{t(`${COPY_PREFIX}.detail.method`)}</dt>
          <dd>
            {check.verificationMethod
              ? t(`${COPY_PREFIX}.method.${check.verificationMethod}`, {}, check.verificationMethod)
              : "—"}
          </dd>
        </div>
        <div>
          <dt>{t(`${COPY_PREFIX}.detail.evidence`)}</dt>
          <dd>{check.evidenceCount || 0}</dd>
        </div>
      </dl>

      {!isComplete ? (
        <div className="cvd-verification-check__actions">
          <label className="cvd-field" htmlFor={`method-${check.id}`}>
            {t(`${COPY_PREFIX}.detail.method`)}
            <select id={`method-${check.id}`} value={method} onChange={(e) => setMethod(e.target.value)} disabled={busy}>
              {METHODS.map((item) => (
                <option key={item} value={item}>
                  {t(`${COPY_PREFIX}.method.${item}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="cvd-field" htmlFor={`notes-${check.id}`}>
            {t(`${COPY_PREFIX}.detail.notesPlaceholder`)}
            <input
              id={`notes-${check.id}`}
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t(`${COPY_PREFIX}.detail.notesPlaceholder`)}
              disabled={busy}
            />
          </label>
          <div className="cvd-verification-check__buttons">
            <button type="button" className="cvd-btn cvd-btn--primary" disabled={busy} onClick={passCheck}>
              {t(`${COPY_PREFIX}.detail.pass`)}
            </button>
            <button
              type="button"
              className="cvd-btn cvd-btn--secondary"
              disabled={busy}
              onClick={() => setShowNaForm((open) => !open)}
            >
              {t(`${COPY_PREFIX}.detail.notApplicable`)}
            </button>
          </div>
          {showNaForm ? (
            <div className="cvd-verification-check__na">
              <label className="cvd-field" htmlFor={`na-${check.id}`}>
                {t(`${COPY_PREFIX}.detail.naReasonPlaceholder`)}
                <input
                  id={`na-${check.id}`}
                  type="text"
                  value={naReason}
                  onChange={(e) => setNaReason(e.target.value)}
                  placeholder={t(`${COPY_PREFIX}.detail.naReasonPlaceholder`)}
                  disabled={busy}
                />
              </label>
              <button type="button" className="cvd-btn cvd-btn--ghost" disabled={busy} onClick={markNotApplicable}>
                {t(`${COPY_PREFIX}.detail.notApplicable`)}
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <p className="cvd-verification-check__completed muted">
          {formatDateTime(check.performedAt, locale)}
        </p>
      )}

      {errorMessage ? (
        <p className="cvd-verification-check__error" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </article>
  );
}

export default function ClientVendorDirectoryVerificationDetailPage() {
  const { clientViewId, caseId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { t, locale } = useLocale();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactNotes, setContactNotes] = useState("");

  const basePath = `${serviciosBase}/verificacion`;

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getVerificationCase(clientViewId, caseId);
      setDetail(body);
      setPreview(null);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.detail.loadError`));
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [caseId, clientViewId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const showBanner = (message) => {
    setBannerMessage(message);
    window.setTimeout(() => setBannerMessage(""), 5000);
  };

  const runAction = async (action) => {
    setBusy(true);
    setBannerMessage("");
    try {
      await action();
    } catch (error) {
      showBanner(error.message || t(`${COPY_PREFIX}.detail.actionError`));
    } finally {
      setBusy(false);
    }
  };

  const startCase = () =>
    runAction(async () => {
      await transitionVerificationCase(clientViewId, caseId, { status: "in_progress" });
      await load();
    });

  const markReady = () =>
    runAction(async () => {
      await transitionVerificationCase(clientViewId, caseId, { status: "ready_for_review" });
      await load();
    });

  const runPreview = () =>
    runAction(async () => {
      const body = await previewVerificationDecision(clientViewId, caseId, {
        expectedCaseVersion: detail?.case?.caseVersion,
      });
      setPreview(body.preview);
    });

  const completeCase = () => {
    if (!preview) {
      showBanner(t(`${COPY_PREFIX}.detail.previewRequired`));
      return;
    }
    runAction(async () => {
      await completeVerificationCase(clientViewId, caseId, {
        expectedCaseVersion: detail?.case?.caseVersion,
      });
      await load();
    });
  };

  const logContact = () => {
    if (!contactNotes.trim()) {
      showBanner(t(`${COPY_PREFIX}.detail.contactNotesRequired`));
      return;
    }
    runAction(async () => {
      await addVerificationContactAttempt(clientViewId, caseId, {
        contactType: "phone",
        contactMethod: "phone",
        direction: "outbound",
        outcome: "message_sent",
        notes: contactNotes.trim(),
      });
      setContactNotes("");
      setShowContactForm(false);
      await load();
    });
  };

  if (loading) {
    return (
      <div className="page-content cvd-admin cvd-verification">
        <ClientPortalLoading message={t(`${COPY_PREFIX}.detail.loading`)} />
      </div>
    );
  }

  if (errorMessage && !detail?.case) {
    return (
      <div className="page-content cvd-admin cvd-verification">
        <ClientPortalError message={errorMessage} onRetry={load} />
      </div>
    );
  }

  const verificationCase = detail?.case;
  const readiness = detail?.readiness;
  const requiredCompleted = readiness?.progress?.requiredChecksCompleted || 0;
  const requiredTotal = readiness?.progress?.requiredChecksTotal || 0;

  return (
    <div className="page-content cvd-admin cvd-verification">
      <div className="cvd-admin__header">
        <div>
          <p className="cvd-run-detail__eyebrow">
            {t(`${COPY_PREFIX}.caseType.${verificationCase?.caseType}`, {}, verificationCase?.caseType)}
          </p>
          <h1 className="cvd-admin__title">{verificationCase?.vendorName || verificationCase?.vendorId}</h1>
          <div className="cvd-run-detail__meta">
            <span className={`cvd-badge ${statusBadgeClass(verificationCase?.status)}`}>
              {t(`${COPY_PREFIX}.status.${verificationCase?.status}`, {}, verificationCase?.status)}
            </span>
            <span className={`cvd-badge cvd-badge--draft`}>
              {t(`${COPY_PREFIX}.priority.${verificationCase?.priority || "normal"}`, {}, verificationCase?.priority)}
            </span>
            <span className="muted">
              {t(`${COPY_PREFIX}.detail.category`)}: {verificationCase?.categoryName || "—"}
            </span>
          </div>
        </div>
        <div className="cvd-admin__actions">
          <Link className="cvd-btn cvd-btn--ghost" to={basePath}>
            ← {t(`${COPY_PREFIX}.backToList`)}
          </Link>
        </div>
      </div>

      {bannerMessage ? <div className="cvd-alert cvd-alert--warning">{bannerMessage}</div> : null}
      {errorMessage ? <div className="cvd-alert cvd-alert--warning">{errorMessage}</div> : null}

      <div className="cvd-verification-detail__actions">
        <button type="button" className="cvd-btn cvd-btn--secondary" disabled={busy} onClick={startCase}>
          {t(`${COPY_PREFIX}.detail.start`)}
        </button>
        <button type="button" className="cvd-btn cvd-btn--secondary" disabled={busy} onClick={markReady}>
          {t(`${COPY_PREFIX}.detail.markReady`)}
        </button>
        <button
          type="button"
          className="cvd-btn cvd-btn--ghost"
          disabled={busy}
          onClick={() => setShowContactForm((open) => !open)}
        >
          {t(`${COPY_PREFIX}.detail.logContact`)}
        </button>
        <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.VERIFICATION_APPROVE}>
          <button type="button" className="cvd-btn cvd-btn--secondary" disabled={busy} onClick={runPreview}>
            {t(`${COPY_PREFIX}.detail.preview`)}
          </button>
          <button type="button" className="cvd-btn cvd-btn--primary" disabled={busy} onClick={completeCase}>
            {t(`${COPY_PREFIX}.detail.complete`)}
          </button>
        </VendorDirectoryPermissionGate>
      </div>

      {showContactForm ? (
        <section className="cvd-panel cvd-verification-contact">
          <label className="cvd-field" htmlFor="contact-notes">
            {t(`${COPY_PREFIX}.detail.contactNotesPlaceholder`)}
            <textarea
              id="contact-notes"
              rows={3}
              value={contactNotes}
              onChange={(event) => setContactNotes(event.target.value)}
              placeholder={t(`${COPY_PREFIX}.detail.contactNotesPlaceholder`)}
            />
          </label>
          <button type="button" className="cvd-btn cvd-btn--primary" disabled={busy} onClick={logContact}>
            {t(`${COPY_PREFIX}.detail.logContact`)}
          </button>
        </section>
      ) : null}

      {readiness ? (
        <section className="cvd-panel">
          <h2>{t(`${COPY_PREFIX}.detail.progress`)}</h2>
          <PipelineBar
            label={t(`${COPY_PREFIX}.detail.requiredChecks`)}
            value={requiredCompleted}
            max={requiredTotal}
          />
          {readiness.blockingIssues?.length ? (
            <>
              <h3>{t(`${COPY_PREFIX}.detail.blockingIssues`)}</h3>
              <ul className="cvd-duplicate-signals cvd-duplicate-signals--negative">
                {readiness.blockingIssues.map((issue) => (
                  <li key={issue.code}>{issue.message}</li>
                ))}
              </ul>
            </>
          ) : (
            <p className="muted">
              {t(`${COPY_PREFIX}.detail.readyForDecision`)}:{" "}
              {readiness.isReadyForDecision ? t(`${COPY_PREFIX}.detail.yes`) : t(`${COPY_PREFIX}.detail.no`)}
            </p>
          )}
        </section>
      ) : null}

      {preview ? (
        <section className="cvd-panel">
          <h2>{t(`${COPY_PREFIX}.detail.previewTitle`)}</h2>
          <dl className="cvd-dl">
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.result`)}</dt>
              <dd>{t(`${COPY_PREFIX}.caseResult.${preview.caseResult}`, {}, preview.caseResult)}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.summary`)}</dt>
              <dd>{preview.summaryChange || "—"}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.badgesToIssue`)}</dt>
              <dd>{(preview.badgesToIssue || []).join(", ") || "—"}</dd>
            </div>
          </dl>
        </section>
      ) : null}

      <section className="cvd-panel">
        <h2>{t(`${COPY_PREFIX}.detail.checklist`)}</h2>
        <div className="cvd-verification-checks">
          {(detail?.checks || []).map((check) => (
            <VerificationCheckCard
              key={check.id}
              check={check}
              caseId={caseId}
              clientViewId={clientViewId}
              t={t}
              locale={locale}
              onUpdated={load}
            />
          ))}
        </div>
      </section>

      {detail?.badges?.length ? (
        <section className="cvd-panel">
          <h2>{t(`${COPY_PREFIX}.detail.activeBadges`)}</h2>
          <ul className="cvd-verification-badges">
            {detail.badges.map((badge) => (
              <li key={badge.id}>
                <strong>{badge.publicLabelEs || badge.badgeKey}</strong>
                <span className="muted">{formatDateTime(badge.issuedAt, locale)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
